'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import styles from './WalletSend.module.css'
import Wallet from '@/utils/wallet'
import Input from '@/components/input'
import Button from '@/components/button'
import AmountAdornment from '@/components/amount-adornment'
import axios from 'axios'
import { API_URL } from '@/utils/constants'
import event from '@/utils/event'
import useWalletBalance from '@/utils/hooks/useWalletBalance'
import ChangeArrowsIcon from '@/icons/change-arrows'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import QrScanner from '@/components/qr-scanner'
import SendRecieveIcon from '@/icons/send-recieve'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import useResolveSns from '@/utils/hooks/useResolveSns'
import WalletBalanceDisplay from '../wallet-balance-display'
import FeeSlider from '@/components/fee-slider'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import useDebounce from '@/utils/hooks/useDebounce'
import { NETWORK, NETWORK_NAME } from '@/utils/constants'
import Link from 'next/link'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const BTC = NETWORK
const USD = 'USD'

let calcQueue = 0

const WalletSend: FC<{
	handleModalClose: () => void
	usdAmount?: string
	recipient?: string
}> = (props): ReactElement => {
	const { handleModalClose } = props
	const [loading, setLoading] = React.useState(false)
	const [toError, _setToError] = React.useState(false)
	const [amount, setAmount] = React.useState(props?.usdAmount ? props?.usdAmount : '')
	const [feeRate, setFeeRate] = React.useState<any>(null)
	const [fee, setFee] = React.useState<any>(null)
	const [psbt, setPsbt] = React.useState<any>(null)
	const [error, setError] = React.useState<any>(null)
	const [feeLoading, setFeeLoading] = React.useState<any>(null)

	const {
		value: to,
		handleChange: handleSetTo,
		setValue: setTo,
		resolveSnsEl,
		loading: snsLoading,
		error: snsError,
		sns
	} = useResolveSns(props?.recipient)

	const debouncedFeeRate: number = useDebounce<number>(feeRate as any, 300)
	const debouncedTo = useDebounce(to, 300)
	const feePrice = useSatoshiPrice(fee as any)

	const { data } = useWalletBalance()

	const [inputCurrency, setInputCurrency] = React.useState<string>(props?.usdAmount ? USD : BTC)
	const { exchangeRate, isLoading: loadingRate } = useExchangeRate()
	const { bitcoinAmount, usdAmount } = React.useMemo(() => {
		let usdAmount = amount
		let bitcoinAmount = amount

		const tempAmount = amount?.length > 0 ? parseFloat(amount) : 0

		bitcoinAmount = (tempAmount / exchangeRate).toFixed(8).toString()
		usdAmount = (tempAmount * exchangeRate).toFixed(2).toString()

		return { bitcoinAmount, usdAmount }
	}, [amount, exchangeRate])

	React.useEffect(() => {
		async function run() {
			const to_address = sns?.address || debouncedTo

			if (loading || to_address?.length === 0 || amount?.length === 0) {
				return
			}

			let sats

			if (inputCurrency === USD) {
				sats = parseFloat(((parseFloat(amount) / exchangeRate) * 1e8).toFixed(0))
			} else {
				sats = parseFloat((parseFloat(amount) * 1e8).toFixed(0))
			}

			if (!sats) {
				return
			}

			calcQueue = calcQueue + 1
			setError(null)
			setFeeLoading(true as any)

			try {
				const {
					data: { psbt }
				} = await axios.post(`${API_URL}/wallet/send`, {
					from: Wallet.getTaprootAddress(),
					to: to_address,
					sats,
					public_key: Wallet.getPublicKey(),
					fee_rate: debouncedFeeRate
				})
				calcQueue = calcQueue - 1
				const fee = Wallet.getPSBTFee(psbt)
				setPsbt(psbt)
				setFee(Number(fee))
			} catch (e) {
				calcQueue = calcQueue - 1
				const message = (e as any)?.response?.data?.message || (e as any)?.message

				if (calcQueue === 0) {
					setError(message)
				}
			}

			if (calcQueue === 0) {
				setFeeLoading(false as any)
			}
		}

		run()
	}, [debouncedFeeRate, debouncedTo, amount, exchangeRate, inputCurrency, sns, loading])

	const handleChangeCurrency = React.useCallback(() => {
		if (inputCurrency === USD) {
			setAmount(amount?.length > 0 ? bitcoinAmount : '')
			setInputCurrency(BTC)
		} else if (inputCurrency === BTC) {
			setAmount(amount?.length > 0 ? usdAmount : '')
			setInputCurrency(USD)
		}
	}, [inputCurrency, amount?.length, bitcoinAmount, usdAmount])

	const handleSubmit = React.useCallback(
		async (evt: any) => {
			evt.preventDefault()
			evt.stopPropagation()

			setLoading(true)

			try {
				const rawtx = await Wallet.signPSBTExtractTx(psbt as any)

				const to_address = sns?.address || debouncedTo
				event.emit('open-broadcast-modal', {
					type: 'send',
					address: to_address,
					rawtx
				})
				handleModalClose()
			} catch (e) {
				Wallet.handleError(e)
			}

			setLoading(false)
		},
		[debouncedTo, sns, handleModalClose, psbt]
	)

	const handleSetAmount = React.useCallback((e: any) => {
		setAmount(e.target.value)
	}, [])

	const amountError = amount?.length > 0 && isNaN(Number(amount))

	const handleQRSuccess = (res: any) => setTo(res)
	const handleQRError = () =>
		event.emit('snackbar', { variant: 'error', title: 'Unable to read QR code!' })

	return (
		<div className={styles.root}>
			<div className={styles.header}>
				<div className={styles.sendRcvIconContainer}>
					<SendRecieveIcon width={17} height={20} />
				</div>
			</div>
			<div className={classNames(styles.receive, 'text-lg', 'semibold')}>Send</div>
			<div className={classNames(styles.description, 'text-sm', 'regular')}>
				Send {NETWORK} from your wallet.
			</div>
			<span className={classNames(styles.balanceTitle, 'text-sm', 'regular')}>Balance</span>
			<WalletBalanceDisplay
				confirmedSatoshis={data?.confirmed_balance}
				unconfirmedSatoshis={data?.unconfirmed_balance}
			/>
			<form className={styles.form} onSubmit={handleSubmit}>
				<Input
					className={classNames(styles.input, { [styles.error]: toError })}
					placeholder={`Recipient’s ${NETWORK_NAME} address`}
					value={to}
					onChange={handleSetTo}
					endAdornmentClassName={styles.addressInputEndAdornment}
					endAdornment={<QrScanner onSuccess={handleQRSuccess} onError={handleQRError} />}
				/>
				{resolveSnsEl}
				<Input
					className={classNames(styles.input, { [styles.error]: amountError })}
					placeholder="Amount"
					value={amount}
					endAdornmentClassName={styles.inputEndAdornment}
					endAdornment={<AmountAdornment type={inputCurrency} />}
					onEndAdornmentClick={handleChangeCurrency}
					onChange={handleSetAmount}
				/>
				{!loadingRate && (
					<div
						className={styles.altCurrency}
						onClick={handleChangeCurrency}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(handleChangeCurrency)}
					>
						<p className={styles.altCurrencyLabel}>
							{inputCurrency === BTC
								? '$' + (usdAmount || '0.00')
								: (bitcoinAmount || '0.00000000') + ' ' + BTC}
						</p>
						<ChangeArrowsIcon className={styles.altCurrencyIcon} />
					</div>
				)}
				<div className="border border-border rounded-lg p-3">
					<FeeSlider feeRate={feeRate} setFeeRate={setFeeRate} />
					<div className="flex items-center justify-between mt-4">
						<p className="text-sm font-bold text-foreground/80">Total fee cost</p>
						<div className="flex flex-col justify-right items-right">
							<p className="text-xs font-bold text-right">
								{feePrice} {NETWORK}
							</p>
							<p className="text-sm font-regular text-muted-foreground text-right">
								${(((fee as any) / 1e8) * exchangeRate).toFixed(2)}
							</p>
						</div>
					</div>
				</div>
				<div className="items-center text-muted-foreground text-xs pt-2 pb-1">
					By clicking "Send Now", you agree to the Ordinals Wallet{' '}
					<Link href={`/terms`} className="underline" target="_blank">
						Terms of Service.
					</Link>
				</div>
				{error && <p className="text-red-500">{error}</p>}
				<ButtonGradientEpic
					loading={loading}
					disabled={snsLoading || snsError || feeLoading}
					size="medium"
					type="submit"
				>
					Send Now
				</ButtonGradientEpic>
				<Button
					background="transparent"
					className={classNames(styles.cancelButton, 'text-md', 'semibold')}
					onClick={props.handleModalClose}
				>
					Cancel
				</Button>
			</form>
		</div>
	)
}

export default WalletSend
